import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { Field, Modal } from "../../../components";
import { useStore } from "../../../stores";
import locale from "../../../constants/locale";
import { toast } from "bulma-toast";
import * as yup from "yup";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router";
import routes from "../../../constants/routes";
import { Chat, Message } from "../../../models";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  message: Message;
  chatId: number;
  senderId: string;
  liveCardId: number;
};

const FBPrivateReplyModal = ({
  isOpen,
  onClose,
  message,
  chatId,
  senderId,
  liveCardId,
}: Props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { routeTo } = useStore("bots");
  const { sendNewMessage, selectedChat } = useStore("chats");

  // Check if the comment is within the 7-day window
  const commentDate = new Date(message.createdAt);
  const daysSinceComment = Math.floor(
    (Date.now() - commentDate.getTime()) / (1000 * 60 * 60 * 24)
  );
  const isWithinPrivateReplyWindow = daysSinceComment <= 7;

  return (
    <Formik
      initialValues={{
        message: undefined,
      }}
      validationSchema={yup.object().shape({
        message: yup.string().required(t(locale.fieldRequired)),
      })}
      onSubmit={async (values: any) => {
        try {
          const data = {
            commentId: message.mId,
            commenterId: message.meta?.sender?.id,
            commentText: message.text,
            commentMessageId: message.id,
            message: values.message,
            chat: selectedChat,
          };
          const chat = (await sendNewMessage(
            senderId,
            liveCardId,
            data,
            false,
            message.firstName,
            message.lastName,
            "startFBChat"
          )) as Chat;
          history.push(
            routeTo(routes.chats).replace(":chatId?", `${chat.id as any}`) +
              "?tab=my-queue"
          );
          window.dispatchEvent(new CustomEvent("loadChats"));
          onClose();
        } catch (e: any) {
          console.log("error", e);
          toast({
            message: e.message ?? t(locale.genericError),
            position: "top-right",
            dismissible: true,
            type: "is-danger",
            pauseOnHover: true,
          });
        }
      }}
    >
      {({ isSubmitting, submitForm, resetForm }) => (
        <Modal
          isOpen={isOpen}
          title={t("Send Private Reply to Comment")}
          onClose={() => {
            resetForm();
            onClose();
          }}
          primaryLoading={isSubmitting}
          primaryText={t("Send Private Reply")}
          onPrimary={submitForm}
          allowCardOverflow
          isWideScreenH
        >
          {!isWithinPrivateReplyWindow && (
            <div className="notification is-warning">
              <strong>Policy Notice:</strong> This comment is older than 7 days.
              Private replies are only allowed within 7 days of the original
              comment according to Facebook's Messenger Platform policy.
            </div>
          )}

          <div className="notification is-primary">
            <div className="mb-2">
              <strong>Original Comment:</strong>
            </div>
            <div className="content">
              <p>"{message.text}"</p>
              <small>
                By: {message.firstName} {message.lastName} •
                {daysSinceComment === 0
                  ? " Today"
                  : ` ${daysSinceComment} day${
                      daysSinceComment > 1 ? "s" : ""
                    } ago`}
              </small>
            </div>
          </div>

          <div className="notification is-light">
            <div className="content">
              <p>
                <strong>Facebook Private Reply Policy:</strong>
              </p>
              <ul>
                <li>
                  You can send <strong>one private message</strong> as a reply
                  to this comment
                </li>
                <li>
                  Must be sent within <strong>7 days</strong> of the original
                  comment
                </li>
                <li>
                  The user will receive your message along with a reference to
                  their original comment
                </li>
                <li>
                  This opens a 24-hour messaging window for further conversation
                </li>
              </ul>
            </div>
          </div>

          <div className="columns">
            <div className="column is-full">
              <Field
                name="message"
                label={t("Your Private Reply Message")}
                placeholder="Enter your private reply to this comment..."
                className="custom-input"
                disabled={isSubmitting || !isWithinPrivateReplyWindow}
                useTextArea
                inputProps={{
                  maxLength: 1000,
                  style: { height: 120, resize: "none" },
                }}
              />
              <p className="help">
                This message will be sent privately to the commenter and will
                reference their original comment.
              </p>
            </div>
          </div>

          {!isWithinPrivateReplyWindow && (
            <div className="notification is-danger">
              <strong>Cannot Send Reply:</strong> This comment exceeds the 7-day
              window for private replies. Consider responding publicly to the
              comment instead.
            </div>
          )}
        </Modal>
      )}
    </Formik>
  );
};

export default observer(FBPrivateReplyModal);
